import {css, StyleSheet} from 'aphrodite';
import moment from 'moment';
import React from 'react';

import PrimaryFilterFormSkeleton from 'search/filters/base/PrimaryFilterFormSkeleton';
import {ButtonSize} from 'ui-library/buttons/Button';
import {MenuWidth} from 'ui-library/menus/MenuConstants';
import MenuPopover from 'ui-library/menus/MenuPopover';
import {USER_EDIT_DATE_FORMAT} from 'util/DateConstants';

import FlexibleDateRangePicker, {DateRange, FilterMode, FILTER_MODE} from './FlexibleDateRangePicker';

const LABEL = 'Timestamp';
const DISPLAY_DATE_FORMAT = 'M/D/YYYY';

function formatDateForDisplay(isoDate: string | null): string {
  if (!isoDate) {
    return '';
  }
  const parsed = moment(isoDate, USER_EDIT_DATE_FORMAT, true);
  return parsed.isValid() ? parsed.format(DISPLAY_DATE_FORMAT) : '';
}

function getDropdownToggleText(value: DateRange, mode: FilterMode): string {
  const start = formatDateForDisplay(value.startDate);
  const end = formatDateForDisplay(value.endDate);

  switch (mode) {
    case FILTER_MODE.BETWEEN:
      return start && end ? `${LABEL}: ${start} – ${end}` : LABEL;
    case FILTER_MODE.AFTER:
      return start ? `${LABEL}: After ${start}` : LABEL;
    case FILTER_MODE.BEFORE:
      return end ? `${LABEL}: Before ${end}` : LABEL;
    default:
      return LABEL;
  }
}

export interface AuditLogDateRangeFilterDropdownProps {
  value: DateRange;
  onChange: (value: DateRange) => void;
  mode: FilterMode;
  onModeChange: (mode: FilterMode) => void;
  disabled?: boolean;
}

export default function AuditLogDateRangeFilterDropdown({
  value,
  onChange,
  mode,
  onModeChange,
  disabled = false,
}: AuditLogDateRangeFilterDropdownProps): JSX.Element {
  const dropdownToggleText = getDropdownToggleText(value, mode);
  const isActive = dropdownToggleText !== LABEL;

  return (
    <div>
      <PrimaryFilterFormSkeleton
        buttonSize={ButtonSize.SMALL}
        dropdownToggleText={dropdownToggleText}
        menuOverride={
          <MenuPopover className={css(styles.menuPopover)} width={MenuWidth.FIT_CONTENT}>
            <FlexibleDateRangePicker
              value={value}
              onChange={onChange}
              mode={mode}
              onModeChange={onModeChange}
            />
          </MenuPopover>
        }
        closeOnInsideClick={false}
        isActive={isActive}
        isDisabled={disabled}
        tooltipTitle={null}
      />
    </div>
  );
}

const styles = StyleSheet.create({
  menuPopover: {
    padding: '16px',
  },
});
